import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import AuditTab from "./AuditTab";
import { SubTabs, Card, KV, Pill, Loading, ErrorState, StatCard, EmptyState } from "./AdminUI";

const TABS = [
  { key: "health", label: "Health" },
  { key: "launch", label: "Launch Readiness" },
  { key: "audit", label: "Audit Log" },
];

export default function SystemTab({ initialTab }) {
  const [tab, setTab] = useState(TABS.some(t => t.key === initialTab) ? initialTab : "health");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  return (
    <div data-testid="admin-system-tab">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} testidPrefix="system-tab" />
      {tab === "health" ? <HealthPanel /> : tab === "launch" ? <LaunchPanel /> : <AuditTab />}
    </div>
  );
}

function HealthPanel() {
  const [h, setH] = useState(null);
  const [cap, setCap] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.get("/admin/health").then(r => setH(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
    api.get("/admin/capacity").then(r => setCap(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async (userId) => {
    try { await api.post(`/account/deletion/retry/${userId}`); toast.success("Retry attempted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!h) return <Loading />;

  return (
    <div className="space-y-5" data-testid="system-health">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="green">API: {h.api.status}</Pill>
        <Pill tone={h.database.reachable ? "green" : "red"}>Database: {h.database.reachable ? "reachable" : "unreachable"}</Pill>
        <Pill tone={h.stripe.mode === "live" ? "green" : "amber"}>Stripe: {h.stripe.mode.toUpperCase()}</Pill>
        <Pill tone={h.email.configured ? "green" : "amber"}>Email: {h.email.mode}</Pill>
        <Pill tone={h.domain.configured ? "green" : "amber"}>Domain: {h.domain.configured ? "set" : "not set"}</Pill>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold"
          data-testid="system-health-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>
      <p className="text-[11px] text-[#222]/45">API keys and secrets are never displayed — only configuration status.</p>

      {h.alerts.length > 0 && (
        <Card title={`Unresolved system alerts (${h.alerts.length})`} testid="system-alerts">
          <div className="space-y-2">
            {h.alerts.map((a, i) => (
              <div key={a.id || i} className="border border-red-200 bg-red-50 rounded-xl p-3" data-testid={`system-alert-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Pill tone="red">{a.kind}</Pill>
                  <span className="text-[10px] text-[#222]/45">{a.updated_at ? new Date(a.updated_at).toLocaleString() : ""}</span>
                </div>
                <p className="text-xs text-[#222]/70 break-words">{a.error || a.event_type || "—"}</p>
                {a.user_id && (
                  <button onClick={() => retry(a.user_id)}
                    className="mt-2 text-xs px-3 py-1.5 rounded-full bg-white border border-black/10 font-semibold"
                    data-testid={`system-alert-retry-${i}`}>Retry deletion workflow</button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Account deletion queue" testid="system-deletion-queue">
        {h.deletion_queue.length === 0 ? (
          <p className="text-sm text-[#222]/50">No deletion requests are in progress.</p>
        ) : (
          <div className="space-y-2">
            {h.deletion_queue.map(r => (
              <div key={r.id} className="border border-black/10 rounded-xl p-3 text-xs" data-testid={`deletion-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone={r.status === "completed" ? "green" : "amber"}>{r.status}</Pill>
                  <span className="font-mono">{r.user_id}</span>
                  <span className="text-[#222]/45">requested {new Date(r.requested_at).toLocaleString()}</span>
                </div>
                {r.last_error && <p className="text-red-600 mt-1">{r.last_error}</p>}
                <button onClick={() => retry(r.user_id)}
                  className="mt-2 px-3 py-1.5 rounded-full bg-black/5 font-semibold"
                  data-testid={`deletion-retry-${r.id}`}>Retry</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Stripe" testid="system-stripe">
          <KV label="Mode" value={h.stripe.mode.toUpperCase()} />
          <KV label="API key configured" value={h.stripe.api_key_configured ? "Yes" : "No"} />
          <KV label="Price ID configured" value={h.stripe.price_id_configured ? "Yes" : "No"} />
          <KV label="Webhook secret" value={h.stripe.webhook_secret_configured ? "Configured" : "Not set"} />
          <KV label="Resolved price" value={h.stripe.price?.display || "unavailable"} />
          <KV label="Webhook events processed" value={h.stripe.webhook_events_processed} />
          <KV label="Unresolved webhook failures" value={h.stripe.webhook_failures_unresolved} />
          <p className="text-[11px] text-[#222]/45 mt-2">{h.stripe.note}</p>
        </Card>
        <Card title="Email & domain" testid="system-email">
          <KV label="Provider" value={h.email.provider} />
          <KV label="Delivery mode" value={h.email.mode} />
          <KV label="From address" value={h.email.from} />
          <KV label="Attempts logged" value={h.email.attempts_logged} />
          <KV label="Failures" value={h.email.failures} />
          <KV label="Public app URL" value={h.domain.public_app_url} />
        </Card>
      </div>

      <Card title="Background jobs" testid="system-jobs">
        <KV label="Hourly scheduler" value={h.background_jobs.hourly_scheduler} />
        <KV label="Stuck deletions pending retry" value={h.background_jobs.stuck_deletion_retries_pending} />
      </Card>

      <Card title="Database" testid="system-database">
        <p className="text-xs text-[#222]/50 mb-3">{h.database.kind}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(h.database.collection_counts).map(([k, v]) => (
            <StatCard key={k} label={k.replaceAll("_", " ")} value={v} testid={`db-count-${k}`} />
          ))}
        </div>
      </Card>

      {cap && (
        <Card title="Platform capacity" testid="system-capacity">
          <div className="grid md:grid-cols-2 gap-x-6">
            <div>
              <KV label="Active Projects" value={cap.my_board.projects_active} />
              <KV label="Closed Projects" value={cap.my_board.projects_closed} />
              <KV label="Shared Projects" value={cap.my_board.shared_projects} />
              <KV label="Board capacity (active / closed)" value={`${cap.my_board.active_capacity} / ${cap.my_board.closed_capacity}`} />
            </div>
            <div>
              <KV label="Pro photos" value={cap.media.pro_photo_count} />
              <KV label="Project media" value={cap.media.project_media_count} />
              <KV label="Client storage used" value={`${(cap.media.total_client_storage_bytes / 1048576).toFixed(1)} MB`} />
              <KV label="Video support" value={cap.media.video_support} />
            </div>
          </div>
        </Card>
      )}

      <Card title="Backups" testid="system-backups">
        <Pill tone={h.backups.automatic_backups_enabled ? "green" : "red"}>
          {h.backups.automatic_backups_enabled ? "Enabled" : "Not configured"}
        </Pill>
        <p className="text-xs text-[#222]/60 mt-2">{h.backups.notes}</p>
      </Card>
    </div>
  );
}

function LaunchPanel() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    api.get("/admin/launch-readiness").then(r => setD(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!d) return <Loading />;

  return (
    <div className="space-y-4" data-testid="system-launch">
      <div className="bg-white rounded-2xl px-4 py-3 border border-black/10">
        <p className="text-sm font-semibold mb-1">
          {d.blockers === 0 ? "All checks pass." : `${d.blockers} outstanding item${d.blockers === 1 ? "" : "s"}`}
        </p>
        <p className="text-xs text-[#222]/60">
          Every item is evaluated live from configuration and platform state — nothing is hardcoded, so a
          check clears itself once genuinely resolved. {d.note}
        </p>
      </div>
      {d.checks.length === 0 ? <EmptyState title="No checks" body="Nothing to evaluate." /> : (
        <div className="space-y-2">
          {d.checks.map(c => (
            <div key={c.key}
              className={`rounded-2xl border p-4 ${c.ok ? "bg-white border-[#2E6B57]/25" : "bg-white border-amber-300"}`}
              data-testid={`launch-check-${c.key}`}>
              <div className="flex items-start gap-3">
                {c.ok ? <CheckCircle2 size={18} className="text-[#2E6B57] shrink-0 mt-0.5" />
                  : <XCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{c.label}</p>
                  <p className="text-xs text-[#222]/60 mt-0.5">{c.detail}</p>
                  {!c.ok && (
                    <p className="text-xs text-[#222]/75 mt-2 bg-[#F7F8F7] rounded-lg p-2">
                      <b>What you need to do:</b> {c.owner_action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
